import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface CombinedAgent {
  id: string;
  title: string;
  agent_name: string | null;
  description: string | null;
  organization_id: string;
  created_at: string;
  is_active: boolean;
  avatar_url: string | null;
  type: "regular" | "voice";
}

export const useCombinedAgentList = () => {
  const [agents, setAgents] = useState<CombinedAgent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { organization } = useAuth();
  const { toast } = useToast();

  const fetchAgents = useCallback(async () => {
    if (!organization?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch regular agents
      const { data: regularAgents, error: regularAgentsError } = await supabase
        .from("agents")
        .select("id, title, agent_name, description, organization_id, created_at, is_active, avatar_url")
        .eq("organization_id", organization.id)
        .order("title", { ascending: true });

      if (regularAgentsError) {
        throw regularAgentsError;
      }

      // Fetch voice agents
      const { data: voiceAgents, error: voiceAgentsError } = await supabase
        .from("voice_agents")
        .select("id, title, agent_name, description, organization_id, created_at, is_active, avatar_url")
        .eq("organization_id", organization.id)
        .order("title", { ascending: true });

      if (voiceAgentsError) {
        throw voiceAgentsError;
      }

      // Combine and mark with type
      const combinedAgents: CombinedAgent[] = [
        ...(regularAgents || []).map(agent => ({ ...agent, type: "regular" as const })),
        ...(voiceAgents || []).map(agent => ({ ...agent, type: "voice" as const }))
      ];

      // Sort by title
      combinedAgents.sort((a, b) => (a.title || "").localeCompare(b.title || ""));

      setAgents(combinedAgents);
    } catch (err: any) {
      setError(err.message || "Failed to fetch agents");
      toast({
        variant: "destructive",
        title: "Error fetching agents",
        description: err.message || "An unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  }, [organization?.id, toast]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  return { agents, isLoading, error, refetch: fetchAgents };
}; 